/**
 * The FlexLayout tab factory (ADR-UI-003 §3). Reads `node.getComponent()`,
 * guards it with `isWorkspaceComponentId`, looks it up ONLY in the supplied
 * registry, and renders `UnavailableWorkspaceComponent` for anything else.
 * No `eval`, no dynamic `import(variable)`, no server-supplied component
 * path — the registry map lookup is the only path from a string to a
 * rendered component (decision 6).
 *
 * Takes `registry` and `canDiscover` as parameters rather than importing a
 * global singleton, so this stays testable without mocking app-level state.
 */
import { createElement, Suspense } from "react";
import type { ReactNode } from "react";
import type { TabNode } from "flexlayout-react";
import type { PermissionCode } from "../../app/generated/shell-permission-codes";
import { EmptyState } from "../../ui/empty-state";
import { isWorkspaceComponentId } from "../registry/component-registry";
import type { WorkspaceComponentRegistry } from "../registry/component-registry";
import { WorkspaceErrorBoundary } from "./error-boundary";
import {
  IncidentRequiredBoundary,
  type WorkspaceIncidentAvailability,
} from "./incident-required-boundary";
import { WorkspaceLoadingFallback } from "./loading-fallback";
import { WorkspacePermissionBoundary } from "./permission-boundary";
import { UnavailableWorkspaceComponent } from "./unavailable-component";
import { workspaceText, type WorkspaceLocale } from "../i18n";

export type WorkspaceTabFactory = (node: TabNode) => ReactNode;

export function createWorkspaceFactory(
  registry: WorkspaceComponentRegistry,
  canDiscover: (required: readonly PermissionCode[]) => boolean,
  locale: WorkspaceLocale = "en",
  incidentSelected = true,
  incidentAvailability?: WorkspaceIncidentAvailability,
): WorkspaceTabFactory {
  return function workspaceFactory(node: TabNode): ReactNode {
    const componentId = node.getComponent();
    const definition =
      componentId !== undefined && isWorkspaceComponentId(componentId)
        ? registry.get(componentId)
        : undefined;

    if (definition === undefined) {
      return <UnavailableWorkspaceComponent componentId={componentId ?? ""} />;
    }

    return (
      <WorkspacePermissionBoundary
        canDiscover={canDiscover}
        locale={locale}
        required={definition.requiredPermissions}
      >
        <div className="thw-workspace-panel">
          <div className="thw-workspace-panel__body" tabIndex={0}>
            {definition.requiresIncident === true && !incidentSelected ? (
              <IncidentRequiredBoundary
                availability={incidentAvailability}
                locale={locale}
              />
            ) : (
              <WorkspaceErrorBoundary componentId={definition.id}>
                <Suspense fallback={<WorkspaceLoadingFallback />}>
                  {createElement(definition.lazyComponent, {
                    config: node.getConfig() as unknown,
                    tabId: node.getId(),
                  })}
                </Suspense>
              </WorkspaceErrorBoundary>
            )}
          </div>
        </div>
      </WorkspacePermissionBoundary>
    );
  };
}
